/* eslint-disable complexity */
import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Ability } from '@casl/ability';

import { noop } from '@mindtickle/utils';

const REQUIRED_OBJECT_OR_STRING = PropTypes.oneOfType([PropTypes.object, PropTypes.string])
  .isRequired;

const alias = (names, validate) => (props, ...args) => {
  if (!names.split(' ').some(name => props[name])) {
    return validate(props, ...args);
  }
};

export default class Can extends PureComponent {
  static propTypes = {
    I: alias('do', PropTypes.string.isRequired),
    a: alias('on this of an', REQUIRED_OBJECT_OR_STRING),
    an: alias('on this of a', REQUIRED_OBJECT_OR_STRING),
    of: alias('on a this an', REQUIRED_OBJECT_OR_STRING),
    this: alias('on a of an', REQUIRED_OBJECT_OR_STRING),
    do: alias('I', PropTypes.string.isRequired),
    on: alias('this a of an', REQUIRED_OBJECT_OR_STRING),
    not: PropTypes.bool,
    passThrough: PropTypes.bool,
    children: PropTypes.any.isRequired,
    ability: PropTypes.instanceOf(Ability).isRequired,
    globalAbility: PropTypes.instanceOf(Ability).isRequired,
  };

  static defaultProps = {};

  constructor(...args) {
    super(...args);
    this.unsubscribeFromAbility = noop;
    this._isAllowed = false;
    this._isGloballyAllowed = false;
    this._ability = null;
  }

  componentWillUnmount() {
    this.unsubscribeFromAbility();
  }

  connectToAbility(ability) {
    if (ability === this._ability) {
      return;
    }

    this.unsubscribeFromAbility();
    this._ability = null;

    if (ability) {
      this._ability = ability;
      this.unsubscribeFromAbility = ability.on('updated', () => this.forceUpdate());
    }
  }

  get allowed() {
    return this._isAllowed;
  }

  isAllowed() {
    const params = this.props;
    const [action, field] = (params.I || params.do).split(/\s+/);
    const subject = params.of || params.a || params.an || params.this || params.on;
    const can = params.not ? 'cannot' : 'can';

    return [
      params.ability[can](action, subject, field),
      params.globalAbility[can](action, subject, field),
    ];
  }

  render() {
    const { ability, passThrough } = this.props;
    this.connectToAbility(ability);
    [this._isAllowed, this._isGloballyAllowed] = this.isAllowed();
    return passThrough || this._isGloballyAllowed ? this.renderChildren() : null;
  }

  renderChildren() {
    const { children, ability } = this.props;
    const disabledByPermission = !this._isAllowed && this._isGloballyAllowed;
    if (typeof children === 'function') {
      return children(disabledByPermission, this._isAllowed, this._isGloballyAllowed, ability);
    } else {
      return <>{children}</>;
    }
  }
}
